import { useLocalization } from '@components/Localization'
import CtaButtons from '../CtaButtons'
import ProductSlider from '../Sliders/ProductSlider'
import Thumbnails from '../Sliders/ProductSlider/Thumbnails'
import { Box, Grid, theme, CleanButton } from '@gassan-ui'
import { Heading, Text } from '@rolex-ui'
import React, { FC, useState, useEffect } from 'react'
import { formatPrice } from '@lib/formatPrice'
import { getKontentImage } from '@lib/kontent'
import bgGoldDesktop from './assets/rolex_model_background_gold_landscape.jpg'
import bgGoldMobile from './assets/rolex_model_background_gold_portrait.jpg'
import bgGreyDesktop from './assets/rolex_model_background_grey_landscape.jpg'
import bgGreyMobile from './assets/rolex_model_background_grey_portrait.jpg'
import bgPinkDesktop from './assets/rolex_model_background_pink_landscape.jpg'
import bgPinkMobile from './assets/rolex_model_background_pink_portrait.jpg'
import { rem } from 'polished'
import Indicators from '../Sliders/Indicators'
import Price from './Price'

const goldGradients = [bgGoldMobile, bgGoldDesktop]
const greyGradients = [bgGreyMobile, bgGreyDesktop]
const pinkGradients = [bgPinkMobile, bgPinkDesktop]

const gradients = {
  'Goudkleurige achtergrond': goldGradients,
  'Grijze achtergrond': greyGradients,
  'Roze achtergrond': pinkGradients,
  'Pink background': pinkGradients,
  'Gold background': goldGradients,
  'Grey background': greyGradients
}

type Props = {
  preTitle: string
  title: string
  price: number | null
  shortDescription: string
  sources: string[]
  background: string
}

const ProductInfo: FC<Props> = props => {
  const modelGradient = gradients[props.background]
  const [showPrice, setShowPrice] = useState(false)
  const [index, setIndex] = useState(0)
  const { messages, lang } = useLocalization()
  const handleGoToAvailability = () => {
    const availableEl = document.getElementById('model-availability')
    if (availableEl && availableEl.offsetTop) {
      window.scrollTo({ top: availableEl.offsetTop - 80, left: 0, behavior: 'smooth' })
    }
  }
  const formattedPrice = props.price ? formatPrice(props.price * 100) : null

  useEffect(() => {
    if (!showPrice && document.body.classList.contains('pageDisplay')) {
      setShowPrice(true)
    }
  }, []) // eslint-disable-line

  return (
    <Box
      bg="white"
      backgroundImage={modelGradient && { large: `url(${modelGradient[1]})` }}
      backgroundSize="cover"
      backgroundRepeat="no-repeat"
      m="0 auto"
      py={[0, '30px', 0]}
      pt={[30, 0]}
      color={{ _: theme.rolex.colors.shades[900], large: 'white' }}
    >
      <Grid gridTemplateColumns={{ _: '100%', large: 'repeat(2, 1fr)' }}>
        <Box display={{ _: 'block', large: 'none' }} textAlign="center" mb={['1.25rem', '30px']}>
          <Heading as="h2" variant="verySmallTitle" color="currentColor" mb={[rem(10), '1.25rem']}>
            {props.preTitle}
          </Heading>
          <Heading as="h1" variant="mediumTitle" mb={rem(10)} color="currentColor">
            {props.title}
          </Heading>
          <Text mt="10px" mb="4px" color="currentColor">
            {props.shortDescription}
          </Text>
          {showPrice && <Price formattedPrice={formattedPrice} />}

          <CleanButton onClick={() => handleGoToAvailability()}>
            <Text as="span" color="currentColor" style={{ textDecoration: 'underline' }}>
              {lang === 'nl' ? 'Model beschikbaarheid' : 'Model availability'}
            </Text>
          </CleanButton>
        </Box>
        <Box
          display={{ _: 'block', large: 'flex' }}
          alignItems="center"
          justifyContent="center"
          width="100%"
        >
          <ProductSlider
            modelGradient={modelGradient}
            activeIndex={index}
            onChange={setIndex}
            sources={props.sources.map(url => getKontentImage(url, { width: 750, height: 750 }))}
            title={`Rolex ${props.title}`}
          />
          <Box display={{ _: 'block', large: 'none' }} mt={['1.25rem']}>
            <Indicators
              mb="0"
              activeIndex={index}
              onClick={goToIndex => {
                setIndex(goToIndex)
              }}
              count={props.sources.length}
            />
          </Box>
        </Box>

        <Box
          display="flex"
          justifyContent={{ _: 'center', large: 'flex-start' }}
          pl={{ large: '10%' }}
          alignItems="center"
        >
          <Box>
            <Box display={{ _: 'none', large: 'block' }}>
              <Heading as="h2" variant="verySmallTitle" color="currentColor" mb="1.25rem">
                {props.preTitle}
              </Heading>
              <Heading as="h1" variant="mediumTitle" color="currentColor" mb={rem(10)}>
                {props.title}
              </Heading>
              <Text mt="10px" mb="4px" color="currentColor">
                {props.shortDescription}
              </Text>
              {showPrice && <Price formattedPrice={formattedPrice} />}
              <CleanButton onClick={() => handleGoToAvailability()}>
                <Text as="span" color="currentColor" style={{ textDecoration: 'underline' }}>
                  {lang === 'nl' ? 'Model beschikbaarheid' : 'Model availability'}
                </Text>
              </CleanButton>
            </Box>
            <Box mt={20} mb={30}>
              <Heading
                textAlign="center"
                as="h2"
                mb="1.25rem"
                variant="verySmallTitle"
                color="currentColor"
                display={{ _: 'block', large: 'none' }}
              >
                {messages.rolex.detail.contactUs}
              </Heading>
              <CtaButtons phoneNumber="+31 (0)20 622 5333" sendMessage findStore />
            </Box>
            <Box display={{ _: 'none', large: 'contents' }}>
              <Thumbnails
                activeIndex={index}
                setIndex={setIndex}
                sources={props.sources.map(url =>
                  getKontentImage(url, { width: 100, height: 100 })
                )}
              />
            </Box>
          </Box>
        </Box>
      </Grid>
    </Box>
  )
}

export default ProductInfo
